/**
 * §24.4 kontrol kataloğu + §24.7 sunum kuralları — SAF modül.
 *
 * React, fetch ve DOM YOKTUR: sorun tablosunun sırası (ceza puanına göre azalan) ve
 * skor kartının renk eşiği panelin iki "yanlış olursa sessizce yanlış" davranışıdır;
 * sayfanın içine gömülü kaldıklarında ancak jsdom testiyle doğrulanabilirlerdi. Burada
 * doğrudan ünite testi yazılır (`seo-checks.test.ts`).
 *
 * Ceza puanları §24.4 tablosunun BİREBİR kopyasıdır. Skoru crawler hesaplar (site_skoru),
 * panel yeniden hesaplamaz — bu tablo yalnızca SIRALAMA ve etiketleme içindir; iki ayrı
 * hesap iki farklı sayı üretirse tek gerçek kaynak crawler'dır.
 */

/** §24.4 — kontrol kimlikleri (crawler `SeoCheckId` taslağıyla aynı sıra). */
export const SEO_CHECK_IDS = [
  'TITLE_MISSING',
  'TITLE_LEN',
  'DESC_MISSING',
  'DESC_LEN',
  'H1_MISSING',
  'H1_MULTI',
  'CANONICAL_MISSING',
  'IMG_ALT',
  'REDIRECT_CHAIN',
  'NOINDEX_UNEXPECTED',
  'JSONLD_INVALID',
  'SLOW_TTFB',
  'SITEMAP_MISSING',
  'ROBOTS_MISSING',
  'BROKEN_INTERNAL',
] as const;

export type SeoCheckId = (typeof SEO_CHECK_IDS)[number];

/** §24.4 — kontrol sayfaya mı taramaya mı yazılır (site-geneli olanın örnek URL'i YOKTUR). */
export type SeoCheckScope = 'page' | 'site';

export interface SeoCheckMeta {
  id: SeoCheckId;
  /** Panelde görünen Türkçe ad. */
  label: string;
  /** §24.4 ceza puanı — sıralamanın tek ölçütü. */
  penalty: number;
  scope: SeoCheckScope;
  /** Koşulun tek cümlelik karşılığı (tablo satırının açıklaması). */
  description: string;
}

/**
 * §24.4 ceza tablosu.
 *
 * IMG_ALT bilinçli olarak 5'tir: gerçek ceza oransaldır (`round(oran × 5)`, sayfa başına
 * değişir) ama sorun tablosu SUMMARY üzerinden çalışır ve orada yalnız sayfa sayısı
 * vardır. Sıralamada kontrolün ÜST SINIRI kullanılır; alt sınır (0) alınsaydı tamamen
 * alt'sız bir galeri sitesi listenin en dibinde kalırdı.
 *
 * BROKEN_INTERNAL 0'dır (§24.4 KARARI: skora girmez, kanıtı R16'ya taşınır) — bu yüzden
 * tabloda en sonda görünür, ama LİSTELENİR: kırık link kullanıcıya görünmese de gerçek
 * bir kusurdur.
 */
export const SEO_CHECKS: Record<SeoCheckId, SeoCheckMeta> = {
  TITLE_MISSING: {
    id: 'TITLE_MISSING',
    label: 'Başlık yok',
    penalty: 8,
    scope: 'page',
    description: '<title> etiketi yok veya boş',
  },
  NOINDEX_UNEXPECTED: {
    id: 'NOINDEX_UNEXPECTED',
    label: 'Beklenmedik noindex',
    penalty: 8,
    scope: 'page',
    description: 'robots meta noindex, ama sayfaya iç linkten ulaşılıyor',
  },
  DESC_MISSING: {
    id: 'DESC_MISSING',
    label: 'Açıklama yok',
    penalty: 6,
    scope: 'page',
    description: 'meta[name=description] yok veya boş',
  },
  H1_MISSING: {
    id: 'H1_MISSING',
    label: 'H1 yok',
    penalty: 5,
    scope: 'page',
    description: 'Sayfada <h1> başlığı yok',
  },
  IMG_ALT: {
    id: 'IMG_ALT',
    label: 'Görsel alt metni eksik',
    penalty: 5,
    scope: 'page',
    description: "alt'sız görsel oranı — ceza oransaldır (en çok 5)",
  },
  SITEMAP_MISSING: {
    id: 'SITEMAP_MISSING',
    label: 'Sitemap yok',
    penalty: 5,
    scope: 'site',
    description: 'Ne /sitemap.xml ne robots.txt Sitemap: satırı var',
  },
  TITLE_LEN: {
    id: 'TITLE_LEN',
    label: 'Başlık uzunluğu',
    penalty: 3,
    scope: 'page',
    description: 'title 10 karakterden kısa veya 60 karakterden uzun',
  },
  CANONICAL_MISSING: {
    id: 'CANONICAL_MISSING',
    label: 'Canonical yok',
    penalty: 3,
    scope: 'page',
    description: 'link[rel=canonical] yok',
  },
  REDIRECT_CHAIN: {
    id: 'REDIRECT_CHAIN',
    label: 'Yönlendirme zinciri',
    penalty: 3,
    scope: 'page',
    description: "Sayfaya ulaşırken 2'den fazla 3xx sıçraması",
  },
  SLOW_TTFB: {
    id: 'SLOW_TTFB',
    label: 'Yavaş TTFB',
    penalty: 3,
    scope: 'page',
    description: 'Tarayıcı isteğinin TTFB süresi 800 ms üzerinde',
  },
  DESC_LEN: {
    id: 'DESC_LEN',
    label: 'Açıklama uzunluğu',
    penalty: 2,
    scope: 'page',
    description: 'description 50 karakterden kısa veya 160 karakterden uzun',
  },
  H1_MULTI: {
    id: 'H1_MULTI',
    label: 'Birden çok H1',
    penalty: 2,
    scope: 'page',
    description: "Sayfada 1'den fazla <h1> var",
  },
  JSONLD_INVALID: {
    id: 'JSONLD_INVALID',
    label: 'Bozuk JSON-LD',
    penalty: 2,
    scope: 'page',
    description: 'application/ld+json bloğu ayrıştırılamıyor',
  },
  ROBOTS_MISSING: {
    id: 'ROBOTS_MISSING',
    label: 'robots.txt yok',
    penalty: 2,
    scope: 'site',
    description: '/robots.txt 200 dönmüyor',
  },
  BROKEN_INTERNAL: {
    id: 'BROKEN_INTERNAL',
    label: 'Kırık iç link',
    penalty: 0,
    scope: 'site',
    description: 'İç linkten gidilen URL 404 döndü (skora girmez, R16 kanıtı)',
  },
};

function knownCheck(id: string): SeoCheckMeta | undefined {
  return (SEO_CHECKS as Record<string, SeoCheckMeta | undefined>)[id];
}

/** §24.4 ceza puanı; katalogda olmayan (crawler'a sonradan eklenmiş) kontrol için 0. */
export function seoCheckPenalty(id: string): number {
  return knownCheck(id)?.penalty ?? 0;
}

/** Türkçe etiket; bilinmeyen kontrol KENDİ kimliğiyle gösterilir — satır gizlenmez. */
export function seoCheckLabel(id: string): string {
  return knownCheck(id)?.label ?? id;
}

/** Bilinmeyen kontrol sayfa kapsamlı varsayılır (örnek URL'i varsa gösterilebilsin). */
export function seoCheckScope(id: string): SeoCheckScope {
  return knownCheck(id)?.scope ?? 'page';
}

export function seoCheckDescription(id: string): string {
  return knownCheck(id)?.description ?? '';
}

/* -------------------------------------------------------------------------- */
/* §24.7 skor kartı renk eşiği                                                  */
/* -------------------------------------------------------------------------- */

/** §24.7 — "≥ 80 yeşil, 60-79 sarı, < 60 kırmızı"; 'none' = skor yok (tarama yapılmamış). */
export type SeoScoreTone = 'good' | 'warn' | 'bad' | 'none';

export const SEO_SCORE_GOOD = 80;
export const SEO_SCORE_WARN = 60;

/**
 * §24.7 renk eşiği. Sınırlar KAPSAYICIDIR: 80 yeşil, 60 sarıdır (şartname "≥ 80" ve
 * "60-79" der). Skor yoksa (tarama hiç koşmadı / bitmedi) 'none' döner — 0 varsayıp
 * kırmızı boyamak "siteniz berbat" demek olurdu, oysa henüz ölçüm YOK.
 */
export function seoScoreTone(score: number | null | undefined): SeoScoreTone {
  if (score === null || score === undefined || !Number.isFinite(score)) return 'none';
  if (score >= SEO_SCORE_GOOD) return 'good';
  if (score >= SEO_SCORE_WARN) return 'warn';
  return 'bad';
}

/* -------------------------------------------------------------------------- */
/* §24.7 sorun tablosu                                                          */
/* -------------------------------------------------------------------------- */

export interface SeoIssueRow {
  id: string;
  label: string;
  /** §24.4 ceza puanı — sıralama ölçütü. */
  penalty: number;
  /** summary'deki sayfa sayısı (site-geneli kontrollerde 1). */
  pages: number;
  scope: SeoCheckScope;
  description: string;
  /** §24.7 — en çok 3 örnek URL; site-geneli kontrolde boş kalır. */
  samples: string[];
}

/**
 * §24.7 sorun tablosu satırları — CEZA PUANINA göre AZALAN.
 *
 * Eşit cezada etkilenen sayfa sayısı (çok sayfa = daha acil), o da eşitse kimlik
 * alfabetik: sıralama her yenilemede AYNI olmalı, aksi hâlde kullanıcı satırların
 * yer değiştirdiğini görür ve tabloya güveni sarsılır.
 *
 * Katalogda olmayan kontrol ATILMAZ (ceza 0 ile en sona düşer): crawler kataloğa yeni
 * bir kontrol eklediğinde panel onu gizlemek yerine ham kimliğiyle gösterir — eksik
 * bilgi, tanımadığı bilgiden kötüdür.
 *
 * Sayfa sayısı 0 (veya negatif) olan kontrol listelenmez: "0 sayfada sorun var" satırı
 * bilgi taşımaz (§24.4'ün "ceza 0 ise listelenmez" ilkesiyle aynı yönde).
 */
export function buildSeoIssueRows(
  summary: Record<string, number> | null | undefined,
  samples: Record<string, string[]> | null | undefined,
): SeoIssueRow[] {
  const rows: SeoIssueRow[] = [];

  for (const [id, rawCount] of Object.entries(summary ?? {})) {
    const pages = typeof rawCount === 'number' && Number.isFinite(rawCount) ? rawCount : 0;
    if (pages <= 0) continue;
    rows.push({
      id,
      label: seoCheckLabel(id),
      penalty: seoCheckPenalty(id),
      pages,
      scope: seoCheckScope(id),
      description: seoCheckDescription(id),
      samples: samples?.[id] ?? [],
    });
  }

  return rows.sort((a, b) => {
    if (b.penalty !== a.penalty) return b.penalty - a.penalty;
    if (b.pages !== a.pages) return b.pages - a.pages;
    return a.id.localeCompare(b.id);
  });
}

/** §24.7 istatistik şeridi — sorun tablosundaki toplam etkilenen sayfa satırı sayısı. */
export function totalAffectedPages(rows: readonly SeoIssueRow[]): number {
  return rows.reduce((sum, row) => sum + row.pages, 0);
}
